import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { jwtDecode } from "jwt-decode";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import NotificationRowsEditor from "./NotificationRowsEditor";
import ContainerStatusDialog from "./ContainerStatusDialog";
import { MILESTONE_LABELS, STATUS_LABELS, UI, alertText, clockSummary, formatDateTime, typeDescription } from "./format";

/*
 * File > Notifications (ocean, air, road). Three views:
 *  - Durum:   container dates (carrier feed or manual), free time and open alerts;
 *             for an air / road file its own dates (file and AMBL / RMBL) and open alerts
 *  - Ayarlar: who is told what, how and when (copied from the customer profile)
 *  - Geçmiş:  every notification created, and customer messages waiting for approval
 */

const currentUserId = () => {
    try {
        return String(jwtDecode(localStorage.getItem("token") || "")?.id || "");
    } catch {
        return "";
    }
};

const errorText = (e, fallback) => e?.errors?.[0]?.detail || e?.message || fallback;

const CARD_SX = { border: `1px solid ${UI.border}`, borderRadius: "12px", backgroundColor: "#fff" };
const SECONDARY_BUTTON_SX = { borderRadius: "10px", color: `${UI.text} !important`, borderColor: "#cbd5e1", textTransform: "none" };
const COLOR_TONE = { success: "success", warning: "warning", error: "danger", info: "info", default: "neutral" };

// Furthest milestone a container has reached, as a short status.
const CONTAINER_STATE = [
    ["EMPTY_RETURN", "Boş iade edildi", "success"],
    ["GATE_OUT", "Terminalden çıktı", "info"],
    ["AVAILABLE", "Çekime hazır", "info"],
    ["DISCHARGE", "Tahliye edildi", "warning"],
    ["ARRIVE_POD", "Limanda", "info"],
    ["COB", "Yolda", "info"],
];

// Air / road file: furthest milestone reached.
const FILE_STATE = [
    ["POD_RECEIVED", "POD alındı", "success"],
    ["DELIVERED", "Teslim edildi", "success"],
    ["ARRIVE_POD", "Varış havalimanında", "info"],
    ["DEPARTED", "Yolda", "info"],
];

const fileState = (f) => {
    const hit = FILE_STATE.find(([key]) => f.milestones[key]);
    return hit ? { label: hit[1], tone: hit[2] } : { label: "Hareket etmedi", tone: "neutral" };
};

const SOURCE_TEXT = { FILE: "Dosya", AMBL: "AMBL", RMBL: "RMBL" };

const containerState = (c) => {
    const hit = CONTAINER_STATE.find(([key]) => c.milestones[key]);
    return hit ? { label: hit[1], tone: hit[2] } : { label: "Yüklenmedi", tone: "neutral" };
};

const Pill = ({ tone, children }) => (
    <MDBox
        component="span"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.6,
            whiteSpace: "nowrap",
            color: UI.tone[tone].fg,
            backgroundColor: UI.tone[tone].bg,
        }}
    >
        {children}
    </MDBox>
);

Pill.propTypes = { tone: PropTypes.oneOf(["neutral", "info", "success", "warning", "danger"]).isRequired, children: PropTypes.node.isRequired };

const StatCard = ({ label, value, tone, onClick }) => (
    <MDBox
        onClick={onClick}
        sx={{
            ...CARD_SX,
            px: 2,
            py: 1.5,
            cursor: onClick ? "pointer" : "default",
            borderColor: tone ? UI.tone[tone].border : UI.border,
            backgroundColor: tone ? UI.tone[tone].bg : "#fff",
            transition: "border-color .15s",
            "&:hover": onClick ? { borderColor: "#94a3b8" } : {},
        }}
    >
        <MDTypography variant="caption" display="block" sx={{ color: tone ? UI.tone[tone].fg : UI.muted }}>
            {label}
        </MDTypography>
        <MDTypography variant="h4" fontWeight="medium" sx={{ color: tone ? UI.tone[tone].fg : UI.text, lineHeight: 1.3 }}>
            {value}
        </MDTypography>
    </MDBox>
);

StatCard.defaultProps = { tone: null, onClick: null };
StatCard.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired, tone: PropTypes.string, onClick: PropTypes.func };

// Horizontal milestone track of one container: done steps are filled, the next one is ringed.
// A step with no date before the furthest done one was skipped (e.g. no ready-for-pickup report).
const MilestoneTrack = ({ container, milestones, labels, etaKey }) => {
    const lastDone = milestones.reduce((acc, key, i) => (container.milestones[key] ? i : acc), -1);
    const nextIndex = lastDone + 1 < milestones.length ? lastDone + 1 : -1;
    return (
        <MDBox sx={{ overflowX: "auto" }}>
            <MDBox display="grid" gridTemplateColumns={`repeat(${milestones.length}, minmax(110px, 1fr))`} sx={{ minWidth: milestones.length * 110 }}>
                {milestones.map((key, i) => {
                    const m = container.milestones[key];
                    const isNext = i === nextIndex;
                    const lineDone = i < lastDone;
                    let detail = i < lastDone ? "Kayıt yok" : "Bekleniyor";
                    if (m) detail = SOURCE_TEXT[m.source] || (m.manual ? "Manuel" : "Taşıyıcı");
                    else if (key === etaKey && container.eta) detail = `ETA ${formatDateTime(container.eta)}`;
                    return (
                        <MDBox key={key} position="relative" pr={1}>
                            <MDBox display="flex" alignItems="center">
                                <MDBox
                                    sx={{
                                        width: 22,
                                        height: 22,
                                        flexShrink: 0,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: m ? UI.tone.success.fg : "#fff",
                                        border: `2px solid ${m ? UI.tone.success.fg : isNext ? UI.tone.info.fg : "#cbd5e1"}`,
                                        boxShadow: isNext ? `0 0 0 3px ${UI.tone.info.bg}` : "none",
                                    }}
                                >
                                    {m && <CheckIcon sx={{ fontSize: "14px !important", color: "#fff" }} />}
                                </MDBox>
                                {i < milestones.length - 1 && (
                                    <MDBox sx={{ flex: 1, height: 2, mx: 0.75, backgroundColor: lineDone ? UI.tone.success.fg : UI.border }} />
                                )}
                            </MDBox>
                            <Tooltip title={m ? `Kaynak: ${m.source || "-"}${m.reference ? ` · Ref: ${m.reference}` : ""}` : ""}>
                                <MDBox mt={0.75}>
                                    <MDTypography variant="caption" fontWeight="medium" display="block" sx={{ color: m || isNext ? UI.text : UI.muted }}>
                                        {labels[key] || key}
                                    </MDTypography>
                                    <MDTypography variant="caption" display="block" sx={{ color: m ? UI.text : UI.muted, lineHeight: 1.3 }}>
                                        {m ? formatDateTime(m.at) : detail}
                                    </MDTypography>
                                    {m && (
                                        <MDTypography variant="caption" display="block" sx={{ color: UI.muted, fontSize: 11 }}>
                                            {detail}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            </Tooltip>
                        </MDBox>
                    );
                })}
            </MDBox>
        </MDBox>
    );
};

MilestoneTrack.defaultProps = { labels: MILESTONE_LABELS, etaKey: "ARRIVE_POD" };
MilestoneTrack.propTypes = {
    container: PropTypes.object.isRequired,
    milestones: PropTypes.arrayOf(PropTypes.string).isRequired,
    labels: PropTypes.object,
    etaKey: PropTypes.string,
};

const legText = (leg) => {
    if (leg.departed) return `Bağlantı gemisi hareket etti · ${formatDateTime(leg.departed.at)}`;
    if (leg.loaded) return `Bağlantı gemisine yüklendi · ${formatDateTime(leg.loaded.at)}`;
    if (leg.arrived) return `Varış · ${formatDateTime(leg.arrived.at)}`;
    return `ETA · ${formatDateTime(leg.eta)}`;
};

export default function ShipmentNotifications({ shipmentId, mode }) {
    const isOcean = mode === "OCEAN";
    const [tab, setTab] = useState(0);
    const [data, setData] = useState(null);
    const [rows, setRows] = useState([]);
    const [rowsKey, setRowsKey] = useState(0);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState(null);
    const meId = useMemo(currentUserId, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await CrudService.getShipmentNotifications(shipmentId, mode);
            setData(response.data);
            setRows(response.data.settings.rows);
            setRowsKey((k) => k + 1);
        } catch (e) {
            setMessage({ severity: "error", text: errorText(e, "Bildirimler yüklenemedi.") });
        } finally {
            setLoading(false);
        }
    }, [shipmentId, mode]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        CrudService.getUsers()
            .then((response) => setUsers((response.data || []).map((u) => ({ id: String(u.id), label: u.attributes?.name || u.attributes?.email || u.id }))))
            .catch(() => setUsers([]));
    }, []);

    const run = async (fn, success) => {
        setBusy(true);
        setMessage(null);
        try {
            await fn();
            if (success) setMessage({ severity: "success", text: success });
            await load();
        } catch (e) {
            setMessage({ severity: "error", text: errorText(e, "İşlem tamamlanamadı.") });
        } finally {
            setBusy(false);
        }
    };

    if (loading && !data) {
        return (
            <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </MDBox>
        );
    }
    if (!data) return message ? <Alert severity={message.severity}>{message.text}</Alert> : null;

    const pending = data.history.filter((l) => l.status === "PENDING_APPROVAL");
    const highAlerts = data.openAlerts.filter((a) => a.priority === "HIGH").length;
    const sentCount = data.history.filter((l) => l.status === "SENT" || l.status === "PARTIAL").length;
    const activeContainers = data.containers.filter((c) => !c.milestones.EMPTY_RETURN).length;
    const dirty = JSON.stringify(rows) !== JSON.stringify(data.settings.rows);
    const file = data.file || null;
    const state = file ? fileState(file) : null;
    // Rule descriptions in this mode's words.
    const types = Object.fromEntries(Object.entries(data.types).map(([type, t]) => [type, { ...t, description: typeDescription(type, mode) }]));
    const meta = {
        milestones: data.milestones,
        clockTypes: data.clockTypes,
        clockEvents: data.clockEvents,
        clockDefaultStart: data.clockDefaultStart,
    };

    return (
        // The page's drawer inherits nowrap/centered text; this panel reads as normal flowing text.
        <MDBox sx={{ whiteSpace: "normal", textAlign: "left" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={1} flexWrap="wrap">
                <MDBox>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="h5" fontWeight="bold" sx={{ color: UI.text }}>
                            Bildirimler
                        </MDTypography>
                        <Pill tone="neutral">Dosya {data.shipment.shipmentNo}</Pill>
                    </MDBox>
                    <MDTypography variant="caption" sx={{ color: UI.muted }}>
                        {data.shipment.customer || "Müşteri seçilmemiş"} · {formatDateTime(data.settings.activatedAt)} tarihinden beri izleniyor
                    </MDTypography>
                </MDBox>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    disabled={busy}
                    sx={SECONDARY_BUTTON_SX}
                    onClick={() =>
                        run(async () => {
                            const r = await CrudService.runShipmentNotifications(shipmentId, mode);
                            setMessage({ severity: "info", text: `${r.data.created} yeni bildirim, ${r.data.pendingApproval} onay bekliyor.` });
                        })
                    }
                >
                    Şimdi değerlendir
                </Button>
            </MDBox>

            {message && (
                <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 1 }}>
                    {message.text}
                </Alert>
            )}

            <MDBox display="grid" gridTemplateColumns={{ xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={1.5} mb={2.5}>
                <StatCard label="Açık uyarı" value={data.openAlerts.length} tone={highAlerts ? "danger" : data.openAlerts.length ? "warning" : null} onClick={() => setTab(0)} />
                <StatCard label="Onay bekleyen" value={pending.length} tone={pending.length ? "warning" : null} onClick={() => setTab(2)} />
                <StatCard label="Gönderilen bildirim" value={sentCount} onClick={() => setTab(2)} />
                {isOcean ? (
                    <StatCard label="Aktif konteyner" value={`${activeContainers} / ${data.containers.length}`} onClick={() => setTab(0)} />
                ) : (
                    <StatCard label="Dosya durumu" value={state?.label || "-"} onClick={() => setTab(0)} />
                )}
            </MDBox>

            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2.5 }}>
                <Tab label={`Durum${data.openAlerts.length ? ` · ${data.openAlerts.length}` : ""}`} />
                <Tab label={`Kurallar${dirty ? " · kaydedilmedi" : ""}`} />
                <Tab label={`Geçmiş${pending.length ? ` · ${pending.length} onay` : ""}`} />
            </Tabs>

            {tab === 0 && (
                <MDBox>
                    <MDBox sx={{ ...CARD_SX, mb: 2.5, overflow: "hidden" }}>
                        <MDBox px={2} py={1.25} display="flex" justifyContent="space-between" alignItems="center" sx={{ backgroundColor: UI.subtle, borderBottom: `1px solid ${UI.border}` }}>
                            <MDTypography variant="button" fontWeight="bold" sx={{ color: UI.text }}>
                                Açık uyarılar
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: UI.muted }}>
                                Aksiyon bekleyen durumlar
                            </MDTypography>
                        </MDBox>
                        {data.openAlerts.length === 0 ? (
                            <MDBox px={2} py={2} display="flex" alignItems="center" gap={1}>
                                <CheckIcon sx={{ color: UI.tone.success.fg }} />
                                <MDTypography variant="button" fontWeight="regular" sx={{ color: UI.text }}>
                                    Aksiyon bekleyen bir durum yok.
                                </MDTypography>
                            </MDBox>
                        ) : (
                            data.openAlerts.map((a, i) => {
                                const high = a.priority === "HIGH";
                                const container = data.containers.find((c) => c.containerNumber === a.containerNumber);
                                return (
                                    <MDBox
                                        key={`${a.type}-${a.containerNumber}-${i}`}
                                        px={2}
                                        py={1.25}
                                        display="flex"
                                        alignItems="center"
                                        gap={1.5}
                                        sx={{ borderTop: i ? `1px solid ${UI.border}` : "none", borderLeft: `3px solid ${high ? UI.tone.danger.fg : UI.tone.warning.fg}` }}
                                    >
                                        {high ? (
                                            <ErrorOutlineIcon sx={{ color: UI.tone.danger.fg }} />
                                        ) : (
                                            <WarningAmberRoundedIcon sx={{ color: UI.tone.warning.fg }} />
                                        )}
                                        <MDBox flex={1} minWidth={0}>
                                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                <MDTypography variant="button" fontWeight="medium" sx={{ color: UI.text }}>
                                                    {a.label}
                                                </MDTypography>
                                                <Pill tone="neutral">{a.containerNumber}</Pill>
                                            </MDBox>
                                            <MDTypography variant="caption" display="block" sx={{ color: UI.muted }}>
                                                {alertText(a)}
                                            </MDTypography>
                                        </MDBox>
                                        {container && (
                                            <Button size="small" variant="outlined" sx={SECONDARY_BUTTON_SX} onClick={() => setEditing(container)}>
                                                Bilgi gir
                                            </Button>
                                        )}
                                    </MDBox>
                                );
                            })
                        )}
                    </MDBox>

                    {!isOcean && file && (
                        <MDBox sx={{ ...CARD_SX, mb: 2, p: 2 }}>
                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <MDTypography variant="h6" sx={{ color: UI.text }}>
                                    {(mode === "AIR" ? [file.airline, file.flight, file.awb && `AWB ${file.awb}`] : [file.plate, file.cmr && `CMR ${file.cmr}`])
                                        .filter(Boolean)
                                        .join(" · ") || data.shipment.shipmentNo}
                                </MDTypography>
                                <Pill tone={state.tone}>{state.label}</Pill>
                                {file.masterNo && <Pill tone="neutral">{`${file.masterName} ${file.masterNo}`}</Pill>}
                            </MDBox>
                            <MDTypography variant="caption" display="block" mb={2} sx={{ color: UI.muted }}>
                                {[file.origin || "-", file.destination || "-"].join(" → ")}
                                {file.etd && !file.milestones.DEPARTED ? ` · ETD ${formatDateTime(file.etd)}` : ""}
                                {file.eta && !file.milestones.DELIVERED && !file.milestones.ARRIVE_POD ? ` · ETA ${formatDateTime(file.eta)}` : ""}
                                {file.rdl && !file.milestones.DELIVERED ? ` · RDL ${formatDateTime(file.rdl)}` : ""}
                            </MDTypography>
                            <MilestoneTrack
                                container={file}
                                milestones={data.milestones}
                                labels={data.milestoneLabels}
                                etaKey={mode === "AIR" ? "ARRIVE_POD" : "DELIVERED"}
                            />
                            <MDTypography variant="caption" display="block" mt={2} pt={1.5} sx={{ color: UI.muted, borderTop: `1px solid ${UI.border}` }}>
                                Tarihler dosyadan ve bağlı {file.masterName} kaydından okunur: kalkış ve ETA önce {file.masterName}&apos;den, teslim ve POD
                                dosyadan. Değiştirmek için ilgili tarih alanını güncelleyip kaydedin.
                            </MDTypography>
                        </MDBox>
                    )}

                    {!isOcean ? null : data.containers.length === 0 ? (
                        <Alert severity="info">Dosyada konteyner numarası yok. Konteyner girildiğinde takip başlar.</Alert>
                    ) : (
                        data.containers.map((c) => {
                            const state = containerState(c);
                            return (
                                <MDBox key={c.containerNumber} sx={{ ...CARD_SX, mb: 2, p: 2 }}>
                                    <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={2} flexWrap="wrap">
                                        <MDBox>
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <MDTypography variant="h6" sx={{ color: UI.text, fontFamily: "monospace", letterSpacing: 0.5 }}>
                                                    {c.containerNumber}
                                                </MDTypography>
                                                <Pill tone={state.tone}>{state.label}</Pill>
                                            </MDBox>
                                            <MDTypography variant="caption" sx={{ color: UI.muted }}>
                                                {[c.carrier, c.terminal].filter(Boolean).join(" · ") || (c.trackingId ? "Taşıyıcı takibi" : "Taşıyıcı takibi yok")}
                                                {c.eta && !c.milestones.ARRIVE_POD ? ` · ETA ${formatDateTime(c.eta)}` : ""}
                                            </MDTypography>
                                        </MDBox>
                                        <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} sx={SECONDARY_BUTTON_SX} onClick={() => setEditing(c)}>
                                            Tarih ve serbest süre
                                        </Button>
                                    </MDBox>

                                    <MilestoneTrack container={c} milestones={data.milestones} />

                                    <MDBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mt={2} pt={2} sx={{ borderTop: `1px solid ${UI.border}` }}>
                                        <MDBox>
                                            <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.75} sx={{ color: UI.muted, textTransform: "uppercase", letterSpacing: 0.4, fontSize: 11 }}>
                                                Aktarma
                                            </MDTypography>
                                            {c.transit.length === 0 ? (
                                                <MDTypography variant="caption" sx={{ color: UI.muted }}>
                                                    Direkt sefer
                                                </MDTypography>
                                            ) : (
                                                c.transit.map((leg) => (
                                                    <MDBox key={leg.locode} display="flex" alignItems="baseline" gap={1} mb={0.5}>
                                                        <MDTypography variant="caption" fontWeight="medium" sx={{ color: UI.text, minWidth: 90 }}>
                                                            {leg.name}
                                                        </MDTypography>
                                                        <MDTypography variant="caption" sx={{ color: UI.muted }}>
                                                            {legText(leg)}
                                                        </MDTypography>
                                                    </MDBox>
                                                ))
                                            )}
                                        </MDBox>
                                        <MDBox>
                                            <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.75} sx={{ color: UI.muted, textTransform: "uppercase", letterSpacing: 0.4, fontSize: 11 }}>
                                                Serbest süre
                                            </MDTypography>
                                            {c.clockStates.length === 0 ? (
                                                <MDTypography variant="caption" sx={{ color: UI.muted }}>
                                                    Tanımlı değil.{" "}
                                                    <MDBox component="span" onClick={() => setEditing(c)} sx={{ color: UI.tone.info.fg, cursor: "pointer", fontWeight: 500 }}>
                                                        Serbest süre ekle
                                                    </MDBox>
                                                </MDTypography>
                                            ) : (
                                                <MDBox display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                                                    {c.clockStates.map((clock) => {
                                                        const s = clockSummary(clock);
                                                        return (
                                                            <Pill key={clock.type} tone={COLOR_TONE[s.color] || "neutral"}>
                                                                {s.label}
                                                            </Pill>
                                                        );
                                                    })}
                                                </MDBox>
                                            )}
                                        </MDBox>
                                    </MDBox>
                                </MDBox>
                            );
                        })
                    )}
                </MDBox>
            )}

            {tab === 1 && (
                <MDBox>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap" mb={2}>
                        <MDTypography variant="caption" sx={{ color: UI.muted, maxWidth: 640 }}>
                            Kurallar {data.settings.originProfile ? "müşteri profilinden" : "varsayılan ayarlardan"} kopyalandı; burada yapılan değişiklik yalnızca bu dosyayı etkiler.
                            İç bildirimler otomatik gider, müşteri bildirimleri müşteri profilinde otomatik açılmadıkça operatör onayıyla gönderilir.
                        </MDTypography>
                    </MDBox>
                    <NotificationRowsEditor
                        key={rowsKey}
                        rows={rows}
                        types={types}
                        users={users}
                        meId={meId}
                        customerContacts={data.customerContacts}
                        onChange={setRows}
                        disabled={busy}
                    />
                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                        px={2}
                        py={1.5}
                        sx={{ ...CARD_SX, position: "sticky", bottom: 0, zIndex: 2, boxShadow: "0 -4px 12px rgba(15, 23, 42, 0.06)" }}
                    >
                        <MDTypography variant="caption" sx={{ color: dirty ? UI.tone.warning.fg : UI.muted, fontWeight: dirty ? 600 : 400 }}>
                            {dirty ? "Kaydedilmemiş değişiklikler var" : "Tüm değişiklikler kaydedildi"}
                        </MDTypography>
                        <MDBox display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            sx={SECONDARY_BUTTON_SX}
                            disabled={busy}
                            onClick={() =>
                                run(() => CrudService.resetShipmentNotificationSettings(shipmentId, mode), "Ayarlar müşteri profiline göre yeniden yüklendi.")
                            }
                        >
                            Müşteri profiline sıfırla
                        </Button>
                        <Button
                            variant="contained"
                            disabled={busy || !dirty}
                            sx={{ color: "#fff !important", borderRadius: "10px", textTransform: "none" }}
                            onClick={() => run(() => CrudService.updateShipmentNotificationSettings(shipmentId, rows, mode), "Bildirim ayarları kaydedildi.")}
                        >
                            Kaydet
                        </Button>
                        </MDBox>
                    </MDBox>
                </MDBox>
            )}

            {tab === 2 && (
                <MDBox sx={{ ...CARD_SX, overflowX: "auto" }}>
                    {data.history.length === 0 ? (
                        <MDBox px={3} py={5} textAlign="center">
                            <MDTypography variant="button" fontWeight="medium" display="block" sx={{ color: UI.text }}>
                                Henüz bildirim oluşmadı
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: UI.muted }}>
                                {formatDateTime(data.settings.activatedAt)} sonrasında gerçekleşen olaylar ve uyarılar burada listelenir.
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <Table
                            size="small"
                            sx={{
                                "& th": { fontSize: 11, fontWeight: 600, color: UI.muted, textTransform: "uppercase", letterSpacing: 0.4, backgroundColor: UI.subtle },
                                "& td, & th": { borderColor: UI.border, py: 1.25 },
                            }}
                        >
                            <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow>
                                    <TableCell>Zaman</TableCell>
                                    <TableCell>Bildirim</TableCell>
                                    <TableCell>{isOcean ? "Konteyner" : "Dosya"}</TableCell>
                                    <TableCell>Alıcılar</TableCell>
                                    <TableCell>Kanal</TableCell>
                                    <TableCell>Durum</TableCell>
                                    <TableCell>Onaylayan</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.history.map((log) => {
                                    const status = STATUS_LABELS[log.status] || { label: log.status, color: "default" };
                                    return (
                                        <TableRow key={log._id} hover>
                                            <TableCell>
                                                <MDTypography variant="caption">{formatDateTime(log.sentAt || log.created_at)}</MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption" fontWeight={log.priority === "HIGH" ? "bold" : "regular"}>
                                                    {data.types[log.type]?.label || log.type}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption">{log.containerNumbers.join(", ")}</MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption" display="block" fontWeight="medium">
                                                    {log.audience === "CUSTOMER" ? "Müşteri" : "İç"}
                                                </MDTypography>
                                                <MDTypography variant="caption" color="text">
                                                    {[...(log.recipients?.emails || []), log.recipients?.users?.length ? `${log.recipients.users.length} kullanıcı` : ""]
                                                        .filter(Boolean)
                                                        .join(", ")}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption">
                                                    {(log.channels || []).map((ch) => (ch === "EMAIL" ? "E-posta" : "Sistem içi")).join(", ") || "-"}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={log.error || ""}>
                                                    <span>
                                                        <Pill tone={COLOR_TONE[status.color] || "neutral"}>{status.label}</Pill>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption">{log.approvedBy?.name || "-"}</MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant={log.status === "PENDING_APPROVAL" ? "contained" : "text"}
                                                    sx={{ textTransform: "none", whiteSpace: "nowrap", color: log.status === "PENDING_APPROVAL" ? "#fff !important" : `${UI.tone.info.fg} !important` }}
                                                    onClick={() =>
                                                        setPreview({
                                                            log,
                                                            subject: log.subject,
                                                            body: log.body,
                                                            emails: (log.recipients?.emails || []).join(", "),
                                                        })
                                                    }
                                                >
                                                    {log.status === "PENDING_APPROVAL" ? "İncele / onayla" : "Görüntüle"}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </MDBox>
            )}

            <ContainerStatusDialog
                open={!!editing}
                container={editing}
                meta={meta}
                suggestedClocks={data.suggestedClocks}
                onClose={() => setEditing(null)}
                onSave={async (containerNumber, payload) => {
                    await CrudService.updateShipmentContainerStatus(shipmentId, containerNumber, payload);
                    setEditing(null);
                    setMessage({ severity: "success", text: `${containerNumber} güncellendi.` });
                    await load();
                }}
            />

            <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="md" fullWidth>
                {preview && (
                    <>
                        <DialogTitle>{data.types[preview.log.type]?.label}</DialogTitle>
                        <DialogContent dividers>
                            {preview.log.status === "PENDING_APPROVAL" ? (
                                <>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Alıcılar"
                                        value={preview.emails}
                                        onChange={(e) => setPreview({ ...preview, emails: e.target.value })}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Konu"
                                        value={preview.subject}
                                        onChange={(e) => setPreview({ ...preview, subject: e.target.value })}
                                        sx={{ mb: 2 }}
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={10}
                                        label="Metin"
                                        value={preview.body}
                                        onChange={(e) => setPreview({ ...preview, body: e.target.value })}
                                    />
                                </>
                            ) : (
                                <>
                                    <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                                        {preview.log.subject}
                                    </MDTypography>
                                    <MDBox component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14, m: 0 }}>
                                        {preview.log.body}
                                    </MDBox>
                                    {preview.log.error && (
                                        <Alert severity="warning" sx={{ mt: 2 }}>
                                            {preview.log.error}
                                        </Alert>
                                    )}
                                </>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPreview(null)}>Kapat</Button>
                            {preview.log.status === "PENDING_APPROVAL" && (
                                <>
                                    <Button
                                        color="error"
                                        disabled={busy}
                                        onClick={() =>
                                            run(async () => {
                                                await CrudService.rejectShipmentNotification(preview.log._id);
                                                setPreview(null);
                                            }, "Bildirim reddedildi.")
                                        }
                                    >
                                        Reddet
                                    </Button>
                                    <Button
                                        variant="contained"
                                        disabled={busy}
                                        sx={{ color: "#fff !important" }}
                                        onClick={() =>
                                            run(async () => {
                                                await CrudService.approveShipmentNotification(preview.log._id, {
                                                    subject: preview.subject,
                                                    body: preview.body,
                                                    emails: preview.emails.split(/[,;\s]+/).filter(Boolean),
                                                });
                                                setPreview(null);
                                            }, "Bildirim müşteriye gönderildi.")
                                        }
                                    >
                                        Onayla ve gönder
                                    </Button>
                                </>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </MDBox>
    );
}

ShipmentNotifications.defaultProps = { mode: "OCEAN" };

ShipmentNotifications.propTypes = {
    shipmentId: PropTypes.string.isRequired,
    mode: PropTypes.oneOf(["OCEAN", "AIR", "ROAD"]),
};
